import { useEffect, useState } from "react";

/**
 * How long a control has to stand still before the server is asked about it.
 *
 * Typing «12» is two keystrokes and the first of them is «1»; typing a name
 * into a search box is five or six, and a table that refetched on each of them
 * would flash its skeleton between the letters. One number for both, because
 * both are the same promise: what is on screen answers what has been typed,
 * not what was half-typed a fifth of a second ago.
 */
export const DEBOUNCE_MS = 250;

/**
 * `value`, but only after it has stood still for `ms`.
 *
 * Identity is the signal a caller reads to know the two disagree: while
 * `useDebounced(x) !== x` the screen is showing an answer to an older
 * question, which is exactly when a button that acts on that answer must not
 * be pressable.
 */
export function useDebounced<T>(value: T, ms: number = DEBOUNCE_MS): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}
